/**
 * @file ReturnPipelinesIndex.tsx
 * @description Заглушка-редирект для `/shop/:slug/orders/returns/pipelines`
 * (phase10.5 шаг 3). Магазин этапа 1 имеет один pipeline (`default_returns`),
 * сразу ведём на редактор стадий default-pipeline'а.
 */

import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router";

import { Alert } from "../../../components/ui/alert";
import { ApiError } from "../../../lib/api-client";
import { listReturnPipelines } from "../../../lib/api-returns";

export function ReturnPipelinesIndex() {
  const { slug } = useParams<{ slug: string }>();
  const [targetId, setTargetId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pipelines = await listReturnPipelines();
        if (cancelled) return;
        const def =
          pipelines.find((p) => p.isDefault && p.isActive) ??
          pipelines.find((p) => p.isActive) ??
          pipelines[0] ??
          null;
        if (!def) {
          setError(
            "В магазине нет ни одного pipeline'а возвратов. Запустите seed (crm:seed) или создайте pipeline.",
          );
          setLoading(false);
          return;
        }
        setTargetId(def.id);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof ApiError
            ? err.message
            : "Не удалось загрузить pipeline'ы возвратов",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="size-6 animate-spin text-on-surface-variant" />
      </div>
    );
  }
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }
  if (!targetId) {
    return <Alert severity="error">Pipeline возвратов не найден.</Alert>;
  }
  return (
    <Navigate
      to={`/shop/${slug}/orders/returns/pipelines/${targetId}/stages`}
      replace
    />
  );
}
